import { hasPermission } from "@edm/shared";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { getSetupStatus } from "@/lib/api";
import { meQueryOptions, queryClient } from "@/lib/auth";
import { AccessoriesPage } from "@/pages/accessories-page";
import { AssetsPage } from "@/pages/assets-page";
import { AttachmentsPage } from "@/pages/attachments-page";
import { AuditPage } from "@/pages/audit-page";
import { ChangePasswordPage } from "@/pages/change-password-page";
import { ConfirmPage } from "@/pages/confirm-page";
import { ConsumablesPage } from "@/pages/consumables-page";
import { DashboardPage } from "@/pages/dashboard-page";
import { DisposalsPage } from "@/pages/disposals-page";
import { InventoryPage } from "@/pages/inventory-page";
import { LicensesPage } from "@/pages/licenses-page";
import { LoginPage } from "@/pages/login-page";
import { MaintenancePage } from "@/pages/maintenance-page";
import { MaterialsPage } from "@/pages/materials-page";
import { PlaceholderPage } from "@/pages/placeholder-page";
import { RemindersPage } from "@/pages/reminders-page";
import { ReportsPage } from "@/pages/reports-page";
import { ApprovalsPage, RequestsPage } from "@/pages/requests-page";
import { SettingsAccessPage } from "@/pages/settings-access-page";
import { SettingsApprovalPage } from "@/pages/settings-approval-page";
import { SettingsCodeRulesPage } from "@/pages/settings-code-rules-page";
import { SettingsEmailPage } from "@/pages/settings-email-page";
import { SettingsEsignPage } from "@/pages/settings-esign-page";
import { SettingsFieldsPage } from "@/pages/settings-fields-page";
import { SettingsLabelPage } from "@/pages/settings-label-page";
import { SettingsMasterDataPage } from "@/pages/settings-master-data-page";
import { SettingsNotificationsPage } from "@/pages/settings-notifications-page";
import { SettingsOrganizationPage } from "@/pages/settings-organization-page";
import { SettingsSecurityPage } from "@/pages/settings-security-page";
import { SettingsSpecialPage } from "@/pages/settings-special-page";
import { SetupPage } from "@/pages/setup-page";
import { StocktakesPage } from "@/pages/stocktakes-page";

interface RouterContext {
  queryClient: QueryClient;
}

function RootComponent() {
  return <Outlet />;
}

function RouterError({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "未知错误";
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-sm font-medium text-red-600">页面加载失败</p>
      <h1 className="text-2xl font-semibold">服务暂时不可用</h1>
      <p className="max-w-lg text-sm text-muted-foreground">{message}</p>
      <Button onClick={() => window.location.reload()}>刷新页面</Button>
    </div>
  );
}

function NotFoundPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-sm font-medium text-muted-foreground">404</p>
      <h1 className="text-2xl font-semibold">页面不存在</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        没有找到 <code className="rounded bg-muted px-1.5 py-0.5">{pathname}</code>。
        该页面可能尚未开放，或地址已变更。
      </p>
      <Button asChild>
        <a href="/">返回工作台</a>
      </Button>
    </div>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (me) throw redirect({ to: "/" });
  },
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  component: SetupPage,
  beforeLoad: async () => {
    const status = await getSetupStatus();
    if (status.bootstrapped) throw redirect({ to: "/login" });
  },
});

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/change-password",
  component: ChangePasswordPage,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me) throw redirect({ to: "/login" });
  },
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  component: AppShell,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me) throw redirect({ to: "/login" });
    if (me.user.mustChangePassword) throw redirect({ to: "/change-password" });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: DashboardPage,
});

const assetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/assets",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:assets")) {
      throw redirect({ to: "/" });
    }
  },
  component: AssetsPage,
});

const materialsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/materials",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:materials")) {
      throw redirect({ to: "/" });
    }
  },
  component: MaterialsPage,
});

const organizationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/organization",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:admin_personnel")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsOrganizationPage,
});

const accessoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accessories",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:accessories")) {
      throw redirect({ to: "/" });
    }
  },
  component: AccessoriesPage,
});

const consumablesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/consumables",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:inventory")) {
      throw redirect({ to: "/" });
    }
  },
  component: ConsumablesPage,
});

const licensesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/licenses",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:licenses")) {
      throw redirect({ to: "/" });
    }
  },
  component: LicensesPage,
});

const inventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:inventory")) {
      throw redirect({ to: "/" });
    }
  },
  component: () => <InventoryPage initialTab="assets" />,
});

const labelTasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/label-tasks",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:label_tasks")) {
      throw redirect({ to: "/" });
    }
  },
  component: lazyRouteComponent(() =>
    import("@/pages/label-tasks-page").then((module) => ({ default: module.LabelTasksPage })),
  ),
});

const approvalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/approvals",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:approvals")) {
      throw redirect({ to: "/" });
    }
  },
  component: ApprovalsPage,
});

const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/requests",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:requests")) {
      throw redirect({ to: "/" });
    }
  },
  component: RequestsPage,
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:audit")) throw redirect({ to: "/" });
  },
  component: AuditPage,
});

const remindersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reminders",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:reminders")) throw redirect({ to: "/" });
  },
  component: RemindersPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:reports")) throw redirect({ to: "/" });
  },
  component: ReportsPage,
});

const attachmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/attachments",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:attachments")) throw redirect({ to: "/" });
  },
  component: AttachmentsPage,
});

const maintenanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/maintenance",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:maintenance")) throw redirect({ to: "/" });
  },
  component: MaintenancePage,
});

const specialRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/special",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:admin_special_actions"))
      throw redirect({ to: "/" });
  },
  component: SettingsSpecialPage,
});

const accessRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/access",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:admin_access")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsAccessPage,
});

const masterDataRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/master-data",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:admin_master_data")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsMasterDataPage,
});

interface PlaceholderPageDefinition {
  path: string;
  title: string;
  description: string;
  milestone: string;
  bullets: string[];
  permission: string;
}

const PLACEHOLDER_PAGES: PlaceholderPageDefinition[] = [];

const placeholderRoutes = PLACEHOLDER_PAGES.map((page) =>
  createRoute({
    getParentRoute: () => appRoute,
    path: page.path,
    beforeLoad: async ({ context }) => {
      const me = await context.queryClient.ensureQueryData(meQueryOptions);
      if (!me || !hasPermission(me.permissions, page.permission)) {
        throw redirect({ to: "/" });
      }
    },
    component: () => (
      <PlaceholderPage
        title={page.title}
        description={page.description}
        milestone={page.milestone}
        bullets={page.bullets}
      />
    ),
  }),
);

const confirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/confirm/$token",
  component: ConfirmPage,
});

const settingsEsignRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/esign",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "admin:access:manage")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsEsignPage,
});

const settingsNotificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/notifications",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "admin:access:manage")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsNotificationsPage,
});

const settingsFieldsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/fields",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "admin:master-data:manage")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsFieldsPage,
});

const settingsApprovalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/approval",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "admin:access:manage")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsApprovalPage,
});

const settingsEmailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/email",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "admin:system:read")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsEmailPage,
});

const disposalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/disposals",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "asset:manage")) {
      throw redirect({ to: "/" });
    }
  },
  component: DisposalsPage,
});

const stocktakesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/stocktakes",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "inventory:manage")) {
      throw redirect({ to: "/" });
    }
  },
  component: StocktakesPage,
});

const settingsSecurityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/security",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:admin_source_whitelist")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsSecurityPage,
});

const settingsCodeRulesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/code-rules",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:admin_code_rules")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsCodeRulesPage,
});

const settingsLabelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/label",
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me || !hasPermission(me.permissions, "menu:admin_label_settings")) {
      throw redirect({ to: "/" });
    }
  },
  component: SettingsLabelPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  setupRoute,
  changePasswordRoute,
  confirmRoute,
  appRoute.addChildren([
    dashboardRoute,
    assetsRoute,
    materialsRoute,
    organizationRoute,
    accessRoute,
    masterDataRoute,
    accessoriesRoute,
    licensesRoute,
    inventoryRoute,
    consumablesRoute,
    labelTasksRoute,
    approvalsRoute,
    requestsRoute,
    auditRoute,
    remindersRoute,
    reportsRoute,
    attachmentsRoute,
    maintenanceRoute,
    specialRoute,
    disposalsRoute,
    settingsEsignRoute,
    settingsNotificationsRoute,
    settingsFieldsRoute,
    settingsApprovalRoute,
    settingsEmailRoute,
    stocktakesRoute,
    settingsSecurityRoute,
    settingsCodeRulesRoute,
    settingsLabelRoute,
    ...placeholderRoutes,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouterError,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
